import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Portfolio, InvestmentType } from '../types';
import { 
  calculateInvestmentSummary, 
  calculateFamilyMemberSummary, 
  formatCurrency, 
  formatPercentage,
  getInvestmentTypeColor 
} from '../utils/calculations';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { TrendingUp, Users, Briefcase, DollarSign, CheckCircle, Circle, ArrowRight, CreditCard } from 'lucide-react';

interface OverviewProps {
  portfolio: Portfolio;
  setPortfolio: (portfolio: Portfolio) => void;
}

const Overview: React.FC<OverviewProps> = ({ portfolio }) => {
  const navigate = useNavigate();
  const summary = calculateInvestmentSummary(portfolio.investments);
  
  // Check if onboarding should be shown (no investments)
  const showOnboarding = portfolio.investments.length === 0;
  
  // Check completion status for each onboarding step
  const onboardingSteps = [
    {
      id: 1,
      title: 'Add Family Members',
      description: 'Add yourself and other family members to track investments separately',
      completed: portfolio.familyMembers.length > 0,
      path: '/family',
      icon: Users,
    },
    {
      id: 2,
      title: 'Add Investments',
      description: 'Upload your CAS PDF or manually enter your investment details',
      completed: portfolio.investments.length > 0,
      path: '/family',
      icon: Briefcase,
    },
    {
      id: 3,
      title: 'Add Cash Flow Details',
      description: 'Add your income sources and track expenses to calculate monthly surplus',
      completed: ((portfolio.incomes?.length || 0) > 0) && ((portfolio.expenses?.length || 0) > 0),
      path: '/income-expenses',
      icon: DollarSign,
    },
  ];
  
  const completedSteps = onboardingSteps.filter(step => step.completed).length;
  const totalSteps = onboardingSteps.length;
  
  // Calculate data for charts
  const investmentsByType = portfolio.investments.reduce((acc, investment) => {
    if (!acc[investment.type]) {
      acc[investment.type] = { invested: 0, current: 0, count: 0 };
    }
    acc[investment.type].invested += investment.investedAmount;
    acc[investment.type].current += investment.currentValue;
    acc[investment.type].count += 1;
    return acc;
  }, {} as Record<InvestmentType, { invested: number; current: number; count: number }>);

  const pieChartData = Object.entries(investmentsByType).map(([type, data]) => ({
    name: type,
    value: data.current,
    color: getInvestmentTypeColor(type as InvestmentType),
  }));

  const barChartData = Object.entries(investmentsByType).map(([type, data]) => ({
    name: type.length > 15 ? type.substring(0, 15) + '...' : type,
    invested: data.invested,
    current: data.current,
    growth: data.current - data.invested,
  }));

  const familyMemberSummaries = portfolio.familyMembers.map(member => 
    calculateFamilyMemberSummary(member, portfolio.investments)
  );

  const stats = [
    {
      title: 'Total Invested',
      value: formatCurrency(summary.totalInvested),
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Current Value',
      value: formatCurrency(summary.totalCurrentValue),
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Family Members',
      value: portfolio.familyMembers.length.toString(),
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Total Investments',
      value: portfolio.investments.length.toString(),
      icon: Briefcase,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  // Show onboarding if no investments
  if (showOnboarding) {
    return (
      <div className="p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <TrendingUp className="h-6 w-6 text-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900">Welcome to HomeGrowInvest!</h2>
            </div>
            <p className="text-gray-600">Get started by completing these setup steps</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Setup Progress</span>
            <span className="text-sm font-semibold text-primary-600">
              {completedSteps} of {totalSteps} completed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Onboarding Steps */}
        <div className="space-y-4">
          {onboardingSteps.map((step) => {
            const Icon = step.icon;
            const StepIcon = step.completed ? CheckCircle : Circle;
            
            return (
              <div
                key={step.id}
                className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
                  step.completed
                    ? 'border-success-200 bg-success-50'
                    : 'border-gray-200 hover:border-primary-300 hover:shadow-md'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 mr-4 ${
                      step.completed ? 'text-success-600' : 'text-gray-400'
                    }`}>
                      <StepIcon className="w-6 h-6" fill={step.completed ? 'currentColor' : 'none'} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className={`text-lg font-semibold ${
                            step.completed ? 'text-success-700' : 'text-gray-900'
                          }`}>
                            Step {step.id}: {step.title}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                        </div>
                        {step.completed && (
                          <span className="ml-4 px-3 py-1 bg-success-100 text-success-700 text-xs font-medium rounded-full">
                            Completed
                          </span>
                        )}
                      </div>
                      {/* Step 3: Show Income and Expenses status */}
                      {step.id === 3 && (
                        <div className="mt-4 grid grid-cols-2 gap-3">
                          <div className={`p-3 rounded-lg border-2 ${
                            (portfolio.incomes?.length || 0) > 0
                              ? 'border-success-300 bg-success-50'
                              : 'border-gray-200 bg-gray-50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">Income</span>
                              <div className={`w-3 h-3 rounded-full ${
                                (portfolio.incomes?.length || 0) > 0
                                  ? 'bg-success-600'
                                  : 'bg-gray-400'
                              }`} />
                            </div>
                            <p className="text-xs text-gray-600 mt-1">
                              {(portfolio.incomes?.length || 0) > 0 ? 'Entered' : 'Not entered'}
                            </p>
                          </div>
                          <div className={`p-3 rounded-lg border-2 ${
                            (portfolio.expenses?.length || 0) > 0
                              ? 'border-success-300 bg-success-50'
                              : 'border-gray-200 bg-gray-50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">Expenses</span>
                              <div className={`w-3 h-3 rounded-full ${
                                (portfolio.expenses?.length || 0) > 0
                                  ? 'bg-success-600'
                                  : 'bg-gray-400'
                              }`} />
                            </div>
                            <p className="text-xs text-gray-600 mt-1">
                              {(portfolio.expenses?.length || 0) > 0 ? 'Entered' : 'Not entered'}
                            </p>
                          </div>
                        </div>
                      )}
                      {!step.completed && (
                        <div className="mt-4">
                          <button
                            onClick={() => navigate(step.path)}
                            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            Get Started
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Completion Message */}
        {completedSteps === totalSteps && (
          <div className="bg-success-50 border-2 border-success-200 rounded-lg p-6">
            <div className="flex items-center">
              <CheckCircle className="w-6 h-6 text-success-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-success-900">
                  🎉 Congratulations! Setup Complete
                </h3>
                <p className="text-sm text-success-700 mt-1">
                  You've completed all setup steps. Your investment overview will appear here once you add investments.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Normal overview when investments exist
  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <TrendingUp className="h-6 w-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-600">Summary of your investments</p>
        </div>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.bgColor} p-3 rounded-lg`}>
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart - Investment Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment Distribution</h3>
          {pieChartData.length > 0 ? (
            <div className="flex flex-col lg:flex-row items-center">
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value as number), name]}
                      labelFormatter={() => ''}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="lg:w-48 w-full mt-4 lg:mt-0 lg:ml-4">
                <div className="space-y-2">
                  {pieChartData.map((entry, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <div 
                        className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="flex-1 truncate" title={entry.name}>
                        {entry.name}
                      </span>
                      <span className="ml-2 font-medium text-gray-600">
                        {((entry.value / pieChartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-300 flex items-center justify-center text-gray-500">
              No investments to display
            </div>
          )}
        </div>

        {/* Bar Chart - Investment Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment Performance</h3>
          {barChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
                <Legend />
                <Bar dataKey="invested" fill="#94a3b8" name="Invested" />
                <Bar dataKey="current" fill="#3b82f6" name="Current Value" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-300 flex items-center justify-center text-gray-500">
              No investments to display
            </div>
          )}
        </div>
      </div>

      {/* Family Member Summary */}
      {familyMemberSummaries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Family Member Summary</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Growth
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Growth %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {familyMemberSummaries.map((memberSummary) => (
                  <tr key={memberSummary.familyMember.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {memberSummary.familyMember.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {memberSummary.familyMember.relationship}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(memberSummary.totalInvested)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(memberSummary.totalCurrentValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(memberSummary.totalGrowth)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        memberSummary.growthPercentage >= 0 ? 'text-success-600' : 'text-danger-600'
                      }`}>
                        {formatPercentage(memberSummary.growthPercentage)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
